import { ButtonHTMLAttributes, ComponentType, forwardRef } from 'react';
import clsx from 'clsx';

import { CircularProgress } from '../';

import { generateTestId } from './data';
import styles from './Button.module.css';

type Falsy = false | 0 | '' | null | undefined;

const iconSize = {
  xsmall: 'h-3 w-3',
  small: 'h-4 w-4',
  medium: 'h-4 w-4',
  large: 'h-5 w-5',
  xlarge: 'h-6 w-6',
};

type Variant = 'contained' | 'outlined' | 'text' | 'icon';
type Color = 'primary' | 'secondary' | 'neutral' | 'error' | 'success' | 'warning' | 'white';
type Size = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  dataTestId: string;
  variant?: Variant;
  color?: Color;
  size?: Size;
  StartIcon?: ComponentType<{ className?: string }> | Falsy;
  isLoading?: boolean;
  align?: 'left' | 'center' | 'right';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props: ButtonProps, ref) => {
  const {
    dataTestId,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    StartIcon,
    isLoading,
    disabled,
    className,
    children,
    align = 'center',
    ...rest
  } = props;

  const testIds = generateTestId(dataTestId);
  return (
    <button
      data-testid={testIds.component}
      type='button'
      {...rest}
      ref={ref}
      disabled={disabled || isLoading}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      className={clsx(className, styles['base'], styles[`${variant}_${color}`], styles[size], styles[`align_${align}`])}
    >
      {isLoading && <CircularProgress className={iconSize[size]} />}
      {!!StartIcon && !isLoading && <StartIcon className={iconSize[size]} />}
      {children}
    </button>
  );
});

Button.displayName = 'Button';
export default Button;
